import React, { useState, useRef, useEffect } from 'react';
import { Container, Row, Col, Button, Card, Badge, Spinner, Carousel } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaperPlane, faHome, faMapLocationDot, faChartLine, faHandshake, faBed, faBath, faRuler, faHeart, faSchool, faCar } from '@fortawesome/free-solid-svg-icons';
import ChatRealEstateConversationalService from '../../services/chat-realestate-conversational.service';
import { useNavigate } from 'react-router-dom';
import './styles/chat-realestate.css';

// Message types
interface Message {
  id: string;
  type: 'user' | 'assistant' | 'listings' | 'system';
  content: string;
  listings?: any[];
  timestamp: Date;
}

interface PropertyPanelProps {
  properties: any[];
  onPropertySelect: (property: any) => void;
  conversationContext?: any;
}

// Property Panel Component - Right side display
const PropertyPanel: React.FC<PropertyPanelProps> = ({ properties, onPropertySelect, conversationContext }) => {
  const [selectedProperty, setSelectedProperty] = useState<any>(null);

  if (!properties || properties.length === 0) {
    return (
      <div className="property-panel-empty text-center p-5">
        <FontAwesomeIcon icon={faHome} size="3x" className="text-muted mb-3" />
        <h5 className="text-muted">Your matches will appear here</h5>
        <p className="text-muted small">
          As we chat, I'll show properties that match what you're looking for
        </p>
      </div>
    );
  }

  return (
    <div className="property-panel">
      <div className="panel-header p-3 border-bottom">
        <h5 className="mb-1">Perfect Matches for You</h5>
        <p className="text-muted small mb-0">Based on our conversation</p>
      </div>
      
      <div className="property-list">
        {properties.map((property, idx) => (
          <div 
            key={property.id} 
            className={`property-item ${selectedProperty?.id === property.id ? 'selected' : ''}`}
            onClick={() => setSelectedProperty(property)}
          >
            <Card className="border-0 shadow-sm mb-3 property-card-interactive">
              {/* Image Carousel for multiple photos */}
              <div className="property-image-container" style={{ height: '200px', overflow: 'hidden' }}>
                {property.images && property.images.length > 1 ? (
                  <Carousel controls={false} indicators={true} interval={null}>
                    {property.images.slice(0, 3).map((image: string, imgIdx: number) => (
                      <Carousel.Item key={imgIdx}>
                        <img 
                          src={image} 
                          alt={`${property.address} - ${imgIdx + 1}`}
                          className="w-100"
                          style={{ height: '200px', objectFit: 'cover' }}
                        />
                      </Carousel.Item>
                    ))}
                  </Carousel>
                ) : (
                  <img 
                    src={property.image || property.images?.[0]} 
                    alt={property.address}
                    className="w-100"
                    style={{ height: '200px', objectFit: 'cover' }}
                  />
                )}
                
                {/* Overlay badges */}
                <div className="position-absolute top-0 start-0 p-2">
                  <Badge bg="primary">##{idx + 1} Match</Badge>
                </div>
                <div className="position-absolute top-0 end-0 p-2">
                  <Button 
                    variant="light" 
                    size="sm" 
                    className="rounded-circle"
                    onClick={(e) => {
                      e.stopPropagation();
                      console.log('Save property:', property.id);
                    }}
                  >
                    <FontAwesomeIcon icon={faHeart} />
                  </Button>
                </div>
              </div>

              <Card.Body>
                <h6 className="text-primary mb-1">{property.price}</h6>
                <p className="small mb-2">{property.address}</p>
                
                {/* Key features */}
                <div className="d-flex gap-3 mb-2">
                  <span className="small">
                    <FontAwesomeIcon icon={faBed} className="me-1" />
                    {property.beds} beds
                  </span>
                  <span className="small">
                    <FontAwesomeIcon icon={faBath} className="me-1" />
                    {property.baths} baths
                  </span>
                  <span className="small">
                    <FontAwesomeIcon icon={faRuler} className="me-1" />
                    {property.sqft} sqft
                  </span>
                </div>
                
                {/* Why it matches - Personalized */}
                <div className="why-matches-box">
                  <p className="small text-success mb-1">
                    <strong>Why this matches:</strong>
                  </p>
                  <p className="small text-muted mb-0">{property.whyItMatches}</p>
                </div>
                
                {/* Context-specific badges */}
                <div className="mt-2">
                  {conversationContext?.schoolDistrict && (
                    <Badge bg="info" className="me-1">
                      <FontAwesomeIcon icon={faSchool} className="me-1" />
                      Great Schools
                    </Badge>
                  )}
                  {conversationContext?.commute && (
                    <Badge bg="warning" text="dark" className="me-1">
                      <FontAwesomeIcon icon={faCar} className="me-1" />
                      Easy Commute
                    </Badge>
                  )}
                </div>
                
                <Button 
                  variant="primary" 
                  size="sm" 
                  className="w-100 mt-2"
                  onClick={() => onPropertySelect(property)}
                >
                  View Details
                </Button>
              </Card.Body>
            </Card>
          </div>
        ))}
      </div>
      
      <div className="panel-footer p-3 border-top">
        <Button variant="outline-primary" className="w-100">
          View All {properties.length}+ Matches
        </Button>
      </div>
    </div>
  );
};

// Minimal header
const MinimalHeader: React.FC = () => {
  return (
    <header className="minimal-header border-bottom" style={{ background: 'white', position: 'sticky', top: 0, zIndex: 1000 }}>
      <Container fluid className="py-2">
        <Row className="align-items-center">
          <Col>
            <div className="d-flex align-items-center">
              <h5 className="mb-0 me-3">chat.realestate</h5>
              <Badge bg="primary" className="me-2">AI-Powered</Badge>
              <small className="text-muted">Find homes by talking, not filtering</small>
            </div>
          </Col>
          <Col xs="auto">
            <Button variant="link" size="sm" className="text-muted">About</Button>
            <Button variant="link" size="sm" className="text-muted">How it works</Button>
            <Button variant="link" size="sm" className="text-muted">Sign in</Button>
          </Col>
        </Row>
      </Container>
    </header>
  );
};

// Suggested prompts
const SuggestedPrompts: React.FC<{ onSelectPrompt: (prompt: string) => void }> = ({ onSelectPrompt }) => {
  const prompts = [
    {
      icon: faHome,
      title: "First-time buyer",
      prompt: "I'm a first-time buyer looking for a starter home"
    },
    {
      icon: faMapLocationDot,
      title: "Relocating for work",
      prompt: "I'm relocating to Phoenix for a new job"
    },
    {
      icon: faChartLine,
      title: "Investment property",
      prompt: "I'm interested in investment properties with good rental potential"
    },
    {
      icon: faHandshake,
      title: "Downsizing",
      prompt: "We're empty nesters looking to downsize"
    }
  ];

  return (
    <div className="suggested-prompts py-5">
      <h4 className="text-center mb-4">What brings you here today?</h4>
      <Row className="g-3">
        {prompts.map((prompt, idx) => (
          <Col md={6} lg={3} key={idx}>
            <Card 
              className="h-100 cursor-pointer hover-shadow"
              onClick={() => onSelectPrompt(prompt.prompt)}
              style={{ cursor: 'pointer', transition: 'all 0.2s' }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <Card.Body className="text-center">
                <FontAwesomeIcon icon={prompt.icon} size="2x" className="text-primary mb-3" />
                <h6>{prompt.title}</h6>
                <small className="text-muted">{prompt.prompt}</small>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

// Mini property card for chat
const ChatPropertyCard: React.FC<{ property: any; onViewDetails: () => void }> = ({ property, onViewDetails }) => {
  return (
    <Card className="chat-property-card shadow-sm" style={{ maxWidth: '350px' }}>
      <Row className="g-0">
        <Col xs={5}>
          <img 
            src={property.image || property.images?.[0]} 
            className="img-fluid rounded-start h-100" 
            alt={property.address}
            style={{ objectFit: 'cover', maxHeight: '120px' }}
          />
        </Col>
        <Col xs={7}>
          <Card.Body className="p-2">
            <h6 className="text-primary mb-1">{property.price}</h6>
            <p className="small mb-1">{property.beds}bd {property.baths}ba • {property.sqft}sqft</p>
            <p className="small text-muted mb-2" style={{ fontSize: '0.75rem' }}>
              {property.address}
            </p>
            <Button 
              variant="outline-primary" 
              size="sm" 
              className="py-0"
              onClick={onViewDetails}
            >
              Details →
            </Button>
          </Card.Body>
        </Col>
      </Row>
    </Card>
  );
};

// Message component
const ChatMessage: React.FC<{ message: Message; onPropertyClick?: (property: any) => void }> = ({ message, onPropertyClick }) => {
  if (message.type === 'user') {
    return (
      <div className="d-flex justify-content-end mb-3">
        <div className="bg-primary text-white rounded-3 px-3 py-2" style={{ maxWidth: '70%' }}>
          {message.content}
        </div>
      </div>
    );
  }

  if (message.type === 'assistant') {
    return (
      <div className="d-flex justify-content-start mb-3">
        <div className="bg-light rounded-3 px-3 py-2" style={{ maxWidth: '70%' }}>
          {message.content}
        </div>
      </div>
    );
  }

  if (message.type === 'listings' && message.listings) {
    // Show top 3 as mini cards in chat
    const topListings = message.listings.slice(0, 3);
    
    return (
      <div className="listings-preview my-3">
        <p className="small text-muted mb-2">Here are your top matches:</p>
        <div className="d-flex flex-column gap-2">
          {topListings.map((property: any, idx: number) => (
            <ChatPropertyCard 
              key={property.id} 
              property={property}
              onViewDetails={() => onPropertyClick?.(property)}
            />
          ))}
        </div>
        {message.listings.length > 3 && (
          <p className="text-center mt-2 mb-0">
            <small className="text-muted">
              + {message.listings.length - 3} more matches shown on the right →
            </small>
          </p>
        )}
      </div>
    );
  }

  return null;
};

// Main component
const HomePageV4: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [allProperties, setAllProperties] = useState<any[]>([]);
  const [conversationInsights, setConversationInsights] = useState<any>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const chatService = useRef(new ChatRealEstateConversationalService());

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSendMessage = async (content: string = inputValue) => {
    if (!content.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: content,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    try {
      const response = await chatService.current.sendMessage(content);
      
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: response.message,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, assistantMessage]);
      
      // Update conversation insights
      const insights = chatService.current.getConversationInsights();
      setConversationInsights(insights);
      
      // If properties returned, update both chat and side panel
      if (response.properties && response.properties.length > 0) {
        setAllProperties(response.properties);
        
        // Add mini cards to chat
        setTimeout(() => {
          const listingsMessage: Message = {
            id: (Date.now() + 2).toString(),
            type: 'listings',
            content: '',
            listings: response.properties,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, listingsMessage]);
        }, 500);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsTyping(false);
    }
  };

  const handleSelectPrompt = (prompt: string) => {
    setInputValue(prompt);
    inputRef.current?.focus();
    handleSendMessage(prompt);
  };

  const handlePropertyView = (property: any) => {
    navigate(`/property/${property.mlsNumber}`);
  };

  return (
    <div className="chat-interface-v4" style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <MinimalHeader />
      
      <Container fluid className="flex-grow-1 d-flex" style={{ maxHeight: 'calc(100vh - 60px)' }}>
        <Row className="w-100 g-0">
          {/* Main chat area - narrower */}
          <Col lg={7} xl={6} className="d-flex flex-column" style={{ height: '100%' }}>
            <div className="flex-grow-1 overflow-auto p-4" style={{ background: '#f8f9fa' }}>
              <Container style={{ maxWidth: '700px' }}>
                {messages.length === 0 && (
                  <>
                    <div className="text-center py-5">
                      <h2 className="mb-3">Let's find your perfect home</h2>
                      <p className="lead text-muted">
                        Tell me about your situation and I'll help you find the right property
                      </p>
                    </div>
                    <SuggestedPrompts onSelectPrompt={handleSelectPrompt} />
                  </>
                )}

                {messages.map((message) => (
                  <ChatMessage 
                    key={message.id} 
                    message={message}
                    onPropertyClick={handlePropertyView}
                  />
                ))}

                {isTyping && (
                  <div className="d-flex justify-content-start mb-3">
                    <div className="bg-light rounded-3 px-3 py-2">
                      <div className="typing-indicator">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </Container>
            </div>

            <div className="border-top p-3" style={{ background: 'white' }}>
              <Container style={{ maxWidth: '700px' }}>
                <form onSubmit={(e) => { e.preventDefault(); handleSendMessage(); }}>
                  <div className="input-group">
                    <input
                      ref={inputRef}
                      type="text"
                      className="form-control form-control-lg"
                      placeholder="Tell me about what you're looking for..."
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                    />
                    <Button 
                      type="submit" 
                      variant="primary"
                      disabled={!inputValue.trim()}
                    >
                      <FontAwesomeIcon icon={faPaperPlane} />
                    </Button>
                  </div>
                </form>
              </Container>
            </div>
          </Col>

          {/* Property Panel - wider */}
          <Col lg={5} xl={6} className="border-start" style={{ background: 'white', height: '100%', overflowY: 'auto' }}>
            <PropertyPanel 
              properties={allProperties}
              onPropertySelect={handlePropertyView}
              conversationContext={conversationInsights?.context?.extractedCriteria}
            />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default HomePageV4;